/* eslint-disable no-unused-vars */
import {
    BusRealTimesResponse,
    LinesResponse,
    RealTime,
    RealTimesResponse,
    Route,
    RouteModel,
    Shape,
    Stop,
    StopModel,
    TimeTableResponse,
} from '../models';
import {toHex} from '../utils';
import {get} from '../utils/fetch.utils';
import {Db} from './../data/db';

// Un singleton de tu base de datos RxDB.
// Asegúrate de inicializarlo una única vez al arrancar la app.
let _db: Db | null = null;
async function getDb(): Promise<Db> {
    if (!_db) {
        _db = await new Db().init();
    }
    return _db;
}


export class LinesApi {
    private static realTimeCache: Map<string, { data: RealTime[], timestamp: number }> = new Map();
    private static CACHE_EXPIRATION_TIME = 60000; // 1min
    // private static TIMETABLE_CACHE_EXPIRATION_TIME = 24 * 60 * 60 * 1000; // 1 día

    static db: any;

    public static async getLines(date?: string): Promise<{ routes: RouteModel[], stops: StopModel[] }> {
        try {
            const data = await get<LinesResponse>(`/gtfs/routes-and-stops${date ? `/${date}` : ''}`);

            return Object.entries(data)
                .reduce((output: { routes: RouteModel[], stops: StopModel[] }, [slug, group]) => {

                    return {
                        routes: [
                            ...output.routes,
                            ...group.routes.map((route) => this.mapRoute(route, slug)),
                        ],
                        stops: [
                            ...output.stops,
                            ...group.stops.map((stop) => this.mapStop(stop, slug)),
                        ],
                    };
                }, {
                    routes: [],
                    stops: [],
                });
        } catch (e) {
            return {
                routes: [],
                stops: [],
            };
        }
    }

    public static async getStopRealTimes(stop: StopModel): Promise<RealTime[]> {
        const NO_REALTIME_SLUGS = ['metro'];
        const cacheKey = `${stop.slug}-${stop.stopCode || stop.stopId}`;
        const now = Date.now();
        if (NO_REALTIME_SLUGS.includes(stop.slug)) {
            return [];
        }
        if (this.realTimeCache.has(cacheKey)) {
            const cachedData = this.realTimeCache.get(cacheKey);
            if (cachedData && now - cachedData.timestamp < this.CACHE_EXPIRATION_TIME) {
                return cachedData.data;
            }
        }

        try {
            const path = stop.slug === 'busamb' ? `/bus/stops/${stop.stopCode}/realtimes` : `/gtfs/${stop.slug}/realtime/${stop.stopId}`;

            const response = stop.slug === 'busamb' ? (await get<BusRealTimesResponse>(path)).times : (await get<RealTime[]>(path)).map((time) => ({
                ...time,
                arrivalTime: Math.floor(time.arrivalTime - Date.now() / 1000),
            }));

            this.realTimeCache.set(cacheKey, {data: response || [], timestamp: now});

            return response || [];
        } catch (e) {
            console.error(`Error en getStopRealTimes: ${e}`);
            return [];
        }
    }

    public static async getStopTimeTable(
        stop: { slug: string; stopId: string },
    ): Promise<TimeTableResponse[]> {

        const db = await getDb();
        const cache = db.dataSources!.timetableCache;

        await cache.removeExpiredEntries();
        const local = await cache.getAllForStop(stop.stopId);
        if (local.length > 0) {
            return local;
        }

        try {
            const response = (await get<TimeTableResponse[]>(
                `/gtfs/${stop.slug}/stops/${stop.stopId}/timetable`,
            )) || [];

            await Promise.all(
                response.map((item) =>
                    cache.saveTimetable(item.stop_id, item.route_id, item.itineraries_timetables),
                ),
            );

            return response;
        } catch (e) {
            console.error(`[LinesApi] Error en getStopTimeTable:`, e);
            return local;
        }
    }

    public static async getRouteShapes(slug: string, shapeId: string): Promise<Shape[]> {

        return await get<Shape[]>(`/gtfs/${slug}/shapes/${shapeId}`);
    }

    public static mapRoute(data: Route, slug: string): RouteModel {
        return {
            id: `${slug}:${data.route.route_id}`,
            slug: slug,
            longName: data.route.route_long_name,
            mapUrl: data.route.map_url,
            timetableUrl: data.route.timetable_url,
            shortName: data.route.route_short_name,
            textColor: toHex(data.route.route_text_color),
            type: data.route.route_type,
            color: toHex(data.route.route_color),
            routeId: data.route.route_id,
            iconShape: data.route.route_icon_shape,
            url: data.route.route_url,
            desc: data.route.route_desc,
            agencyId: data.route.agency_id,
            services: data.services.map((s) => ({
                todayService: s.todayService,
                timetable: s.timetable,
                serviceId: s.service_id,
                description: s.description,
                timetableDescription: s.timetableDescription,
            })),
            tripsPaths: data.tripsPaths.map((tp) => ({
                tripIds: tp.trip_ids,
                stopIds: tp.stop_ids,
                tripHeadsign: tp.trip_headsign,
                directionId: tp.direction_id,
                shapeId: tp.shape_id,
            })),
        };
    }

    public static mapStop(data: Stop, slug: string): StopModel {
        return {
            id: `${slug}:${data.stop_code || data.stop_id}`,
            slug: slug,
            routesIds: data.routes_ids,
            stopName: data.stop_name,
            stopDesc: data.stop_desc,
            stopCode: data.stop_code,
            stopUrl: data.stop_url,
            stopType: data.stop_type,
            parentStation: data.parent_station || '',
            routesTypes: data.routes_types,
            wheelchairBoarding: !!data.wheelchair_boarding,
            stopId: data.stop_id,
            lat: data.stop_lat,
            lng: data.stop_lon,
        };
    };
}
